import { FormControl, Grid, InputLabel, Select, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { throttle } from 'lodash';
import React from 'react';

import { guessPriceDisplay, parsePriceDisplay, PRICE_DISPLAY_EXACT_PRICE, PRICE_DISPLAY_PRICE_LEVEL, PRICE_DISPLAY_PRICE_RANGE, PRICE_TYPE_EXACT_PRICE, priceDisplayHtmlOptions, priceLevelHtmlOptions } from '../activity/utils';
import { ContextStore } from '../store/contextStore';

const PriceDisplay = (props) => {
    const errors = props.errors || {};
    const showPrices = true; // props.object.payment_type === PAYMENT_TYPE_PAY_AT_LOCATION || props.object.payment_type === PAYMENT_TYPE_PREPAID_ADDON;
    const priceDisplay = guessPriceDisplay(props.object);
    const showPriceLevel = showPrices && priceDisplay === PRICE_DISPLAY_PRICE_LEVEL;
    const showCostMin = showPrices && (priceDisplay === PRICE_DISPLAY_EXACT_PRICE || priceDisplay === PRICE_DISPLAY_PRICE_RANGE);
    const showCostMax = showPrices && priceDisplay === PRICE_DISPLAY_PRICE_RANGE;

    const [exactAmountError, setExactAmountError] = React.useState(false);
    const [priceRangeMinAmountError, setPriceRangeMinAmountError] = React.useState(false);
    const [priceRangeMaxAmountError, setPriceRangeMaxAmountError] = React.useState(false);
    const [touched, setTouched] = React.useState({
        cost_min: false,
        cost_max: false,
    });
    const { currencies, currencyMap } = React.useContext(ContextStore);
    const costMinErrorMessage = priceRangeMinAmountError ? 'Min amount must be smaller than max amount' : 'Please enter a valid amount';
    const costMaxErrorMessage = priceRangeMaxAmountError ? 'Max amount must be greater than min amount' : 'Please enter a valid amount';

    React.useEffect(() => {
        const updateErrors = () => {
            setExactAmountError(false);
            setPriceRangeMinAmountError(false);
            setPriceRangeMaxAmountError(false);

            props.setClientErrors && props?.setClientErrors((prev) => ({ ...prev, amountsNotEntered: false }));

            if (priceDisplay === PRICE_DISPLAY_PRICE_RANGE) {
                let hasError = false;
                if (!props.object.cost_min && !props.object.cost_max) {
                    setPriceRangeMinAmountError(true);
                    setPriceRangeMaxAmountError(true);
                    hasError = true;
                } if (props.object.cost_min && props.object.cost_max && (props.object.cost_min > props.object.cost_max)) {
                    setPriceRangeMaxAmountError(true);
                    hasError = true;
                }

                if (hasError) {
                    props.setClientErrors && props?.setClientErrors((prev) => ({ ...prev, amountsNotEntered: true }));
                }
            }

            if (priceDisplay === PRICE_DISPLAY_EXACT_PRICE) {
                if (!props.object.cost_min) {
                    setExactAmountError(true);
                    props.setClientErrors && props?.setClientErrors((prev) => ({ ...prev, amountsNotEntered: true }));
                }
            }
        };

        updateErrors();
    }, [props.object.cost_min, props.object.cost_max, priceDisplay]);


    const onChange = (newObject) => {
        props.onChange && props.onChange(newObject, props.idx);
    };

    const onIntChange = (ev) => {
        onChange({
            ...props.object,
            [ev.target.name]: +ev.target.value,
        });
    };

    const onFloatChange = (ev) => {
        onChange({
            ...props.object,
            [ev.target.name]: parseFloat(ev.target.value),
        });
    };

    const onTextChange = throttle((ev) => {
        onChange({
            ...props.object,
            [ev.target.name]: ev.target.value,
        });
    }, 300);

    const onCurrencyChange = (ev, nv) => {
        onChange({
            ...props.object,
            currency: nv?.id,
        });
    };

    const onPriceDisplayChange = (ev) => {
        const newObject = {
            ...props.object,
            price_display: +ev.target.value,
            // #CP-5150 requested to not nullify the values
            // cost_min: null,
            // cost_max: null,
            // currency: null,
            // cost_prefix: null,
            // cost_unit: null,
            ...parsePriceDisplay(+ev.target.value),
        };
        onChange(newObject);
    };
    const handleBlur = (field) => () => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        // Trigger error check on blur
        if (priceDisplay === PRICE_DISPLAY_PRICE_RANGE) {
            if (!props.object.cost_min && !props.object.cost_max) {
                setPriceRangeMinAmountError(true);
                setPriceRangeMaxAmountError(true);
            } else if (props.object.cost_min && props.object.cost_max && (props.object.cost_min > props.object.cost_max)) {
                setPriceRangeMaxAmountError(true);
            }
        } else if (priceDisplay === PRICE_DISPLAY_EXACT_PRICE) {
            if (!props.object.cost_min) {
                setExactAmountError(true);
            }
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <FormControl variant="filled" size="small" fullWidth disabled={props.disabled}>
                    <InputLabel>Price Display</InputLabel>
                    <Select {...errors.price_display} native name="price_display" value={priceDisplay} onChange={onPriceDisplayChange}>
                        {priceDisplayHtmlOptions}
                    </Select>
                </FormControl>
            </Grid>
            {!showPriceLevel ? <Grid item xs={false} sm={6}></Grid> : null}
            {showPriceLevel ? (
                <Grid item xs={12} sm={3}>
                    <FormControl variant="filled" size="small" fullWidth disabled={props.disabled}>
                        <InputLabel>Price Level</InputLabel>
                        <Select {...errors.price_level} native name="price_level" value={props.object.price_level} onChange={onIntChange}>
                            {priceLevelHtmlOptions}
                        </Select>
                    </FormControl>
                </Grid>
            ) : null}
            {showCostMin && !showCostMax ? (
                <Grid item xs={12} sm={3}>
                    <TextField {...errors.cost_prefix} disabled={props.disabled} name="cost_prefix" value={props.object.cost_prefix} onChange={onTextChange} type="string" variant="filled" size="small" label="Add Cost Prefix" InputLabelProps={{ shrink: true }} fullWidth helperText='e.g., "from", "up to"' />
                </Grid>
            ) : null}
            {showCostMin ? (
                <Grid item xs={12} sm={showCostMax ? 3 : 3}>
                    <TextField
                        {...errors.cost_min}
                        error={(touched.cost_min && exactAmountError) || (touched.cost_min && priceRangeMinAmountError)}
                        helperText={(touched.cost_min && (exactAmountError || priceRangeMinAmountError)) && costMinErrorMessage}
                        onBlur={handleBlur('cost_min')}
                        disabled={props.disabled}
                        name="cost_min"
                        value={props.object.cost_min || ''}
                        onChange={onFloatChange}
                        type="number"
                        variant="filled"
                        size="small"
                        label={priceDisplay === PRICE_TYPE_EXACT_PRICE ? 'Amount' : 'Price Min'}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            ) : null}
            {showCostMax ? (
                <Grid item xs={12} sm={3}>
                    <TextField
                        {...errors.cost_max}
                        disabled={props.disabled}
                        error={touched.cost_max && priceRangeMaxAmountError}
                        helperText={touched.cost_max && priceRangeMaxAmountError && costMaxErrorMessage}
                        onBlur={handleBlur('cost_max')}
                        name="cost_max"
                        value={props.object.cost_max}
                        onChange={onFloatChange}
                        type="number"
                        variant="filled"
                        size="small"
                        label="Price Max"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            ) : null}
            {showCostMin ? (
                <Grid item xs={12} sm={3}>
                    <Autocomplete
                        options={currencies}
                        disabled={props.disabled}
                        noOptionsText={'No matches'}
                        getOptionLabel={(option) => option.iso}
                        renderOption={(props, option) => (
                            <div {...props}>
                                {option.iso} - {option.name}
                            </div>
                        )}
                        renderInput={(params) => <TextField {...params} {...errors.currency} label="Choose Currency" variant="filled" size="small" inputProps={{ ...params.inputProps, autoComplete: 'new-password' }} />}
                        value={props.object.currency ? currencyMap[props.object.currency] : { name: '', iso: '' }}
                        onChange={onCurrencyChange}
                    />
                </Grid>
            ) : null}
            {showCostMin ? (
                <Grid item xs={12} sm={3}>
                    <TextField {...errors.cost_unit} disabled={props.disabled} name="cost_unit" value={props.object.cost_unit} onChange={onTextChange} type="string" variant="filled" size="small" label="Add Cost Unit" fullWidth InputLabelProps={{ shrink: true }} helperText="e.g., per person" />
                </Grid>
            ) : null}
        </Grid>
    );
};

export default PriceDisplay;
